import { Extension, Tag, Loc, Type, Dom, Runtime } from "main.core";

import './palette.css';

export class BasePalette {
    constructor()
    {
        const segments = location.pathname.split('/');
        this.crmType = segments[2];
        if (this.crmType === 'type') {
            this.crmType = segments[3];
        }

        this.preference = JSON.parse(JSON.stringify(Extension.getSettings('sibix.palette').get(`preferences.[sibix_palette_${this.crmType}_colors]`)));

        BX.addCustomEvent("onPullEvent-sibix.palette", BX.delegate(function (command, params) {
            if (command === 'updatePreferenceRules' && params.optionName === `sibix_palette_${this.crmType}_colors`) {
                this.preference = JSON.parse(params.optionValue);

                this.viewRequiredFields(true);
            }
        }, this));

        BX.addCustomEvent("tasksTaskEvent", (method, task) => {
            const item = task.task;
            const column = BX?.CRM?.Kanban?.Grid?.getInstance()?.columns?.[item.columnId];
            if (!column.defaultSetPullItemBackground) {
                column.defaultSetPullItemBackground = column.setPullItemBackground;

                column.setPullItemBackground = (item, backgroundColor) => {
                    column.defaultSetPullItemBackground(item, backgroundColor);

                    const fields = item?.data?.fields;
                    let background = this.#getBackground(fields);
                    if (!!background) {
                        item.container.style.background = background;
                    }
                }
            }
        });

        BX.addCustomEvent("Kanban.Column:Render", this.#handleKanban.bind(this));
        BX.addCustomEvent("Kanban.Column:Render", this.viewRequiredFields.bind(this));
        BX.addCustomEvent("Kanban.Column:Render", this.setFieldsSelector.bind(this));
        BX.addCustomEvent('onAfterPopupShow', this.modifyFieldsSelector.bind(this));
        BX.addCustomEvent('onLocalStorageChange', this.viewRequiredFields.bind(this, true));

        BX.ready(this.renderSettingBtn.bind(this));
    }

    #handleKanban(column): void
    {
        if (!column?.id) {
            return;
        }

        column?.items?.forEach(item => {
            if (!item.container) {
                return;
            }

            const fields = item?.data?.fields;
            let background = this.#getBackground(fields);

            if (!!background) {
                item.container.style.background = background;
            }
        });
    }

    #getBackground(fields): string
    {
        let background = '';

        if (!Type.isArray(this.preference) || !Type.isArray(fields)) {
            return background;
        }

        for (const rule of this.preference) {
            if (
                !Type.isArray(rule?.conditions) ||
                rule?.active === false ||
                rule?.default === true
            ) {
                continue;
            }

            let setBackground = true;
            setBackground = this.#checkConditions(fields, rule.conditions, rule?.conditionsOperator);

            if (setBackground) {
                const action = rule.actions.find(item => item.type === 'setColor');
                if (!!action?.value?.background) {
                    background = action.value.background;
                    break;
                }
                switch (action?.value?.type) {
                    case "gradient":
                        background = `linear-gradient(to right, ${action?.value?.colors[0]} 5%, ${action?.value?.colors[1] ?? '#FFFFFF'} 95%)`;
                        break;
                    default:
                        background = action?.value?.colors[0];
                        break;
                }

                break;
            }
        }

        const defaultRule = this.preference.find(rule => rule?.default)
        if (!background && !!defaultRule && defaultRule?.active === true) {
            const action = defaultRule.actions.find(item => item.type === 'setColor');
            if (!!action?.value?.background) {
                background = action.value.background;
            }
        }

        return background;
    }

    #checkConditions(fields, conditions, conditionsOperator = {}): boolean
    {
        if (conditions.length < 1) {
            return false;
        }

        let setBackground = true;

        for (const condition of conditions) {
            if (conditionsOperator?.value === 'or') {
                setBackground = true;
            }

            if (!!condition['group'] && Type.isArray(condition['group']) ) {
                setBackground = (setBackground && (this.#checkConditions(fields, condition['group'], condition?.conditionsOperator)));
            } else {
                if (!condition.field || !condition.operator || !condition.value) {
                    return false;
                }

                const field = fields.find(field => field?.code === condition.field);

                setBackground = (
                    setBackground &&
                    (
                        (!field?.value && condition.operator === 'notEqual') ||
                        (
                            field &&
                            !!field?.value &&
                            (
                                (condition.operator === 'equal' && field.value === condition.value) ||
                                (condition.operator === 'notEqual' && field.value !== condition.value) ||
                                (condition.operator === 'more' && field.value > condition.value) ||
                                (condition.operator === 'less' && field.value < condition.value)
                            )
                        )
                    )
                );
            }

            if (setBackground && (conditionsOperator?.value === 'or')) {
                break;
            }
        }

        return setBackground;
    }

    renderSettingBtn(): void
    {
        const toolbarButtons = document.querySelector('.ui-toolbar-right-buttons');
        if (toolbarButtons) {
            const button = new BX.UI.Button({
                id: "palette-settings-btn",
                className: 'ui-btn-icon-palette',
                props: {
                    id: "palette-settings-btn"
                },
                onclick: (btn, event) => {
                    Runtime.loadExtension('sibix.palette.preferences').then((response) => {
                        if (!!response.Preferences) {
                            response.Preferences.create().showSettings();
                        } else {
                            console.error('Error in loadExtension "sibix.palette.preferences".');
                        }
                    });
                },
                color: BX.UI.Button.Color.LIGHT_BORDER,
            });
            button.setDependOnTheme(true);
            toolbarButtons.prepend(button.getContainer());
        }
    }

    viewRequiredFields(needApply = false): void
    {
        if (!Type.isBoolean(needApply)) {
            needApply = false;
        }

        const kanbanInstance = BX?.CRM?.Kanban?.Grid?.getInstance();
        if (kanbanInstance && this.preference) {
            const newFields = kanbanInstance.getData().customFields;

            Object.entries(this.preference).forEach(([key, rule]) => {
                if (Type.isArray(rule.conditions)) {
                    rule.conditions.forEach(condition => {
                        if (condition.field && !newFields.includes(condition.field)) {
                            newFields.push(condition.field);
                            needApply = true;
                        }
                    });
                }
            });

            if (needApply) {
                kanbanInstance.fetchFields('view', kanbanInstance.getData().entityType, kanbanInstance.getData().customFields).then((data) => {
                    kanbanInstance.onApplyCheckboxList('view', newFields, data.options);
                });
            }
        }
    }

    setFieldsSelector(): void
    {
        const checkField = (conditions, newFields) => {
            conditions.forEach(condition => {
                if (condition.field && !newFields.includes(condition.field)) {
                    newFields.push(condition.field);
                }
                if (!!condition.group) {
                    checkField(condition.group, newFields);
                }
            });
        }

        const kanbanInstance = BX?.CRM?.Kanban?.Grid?.getInstance();
        if (kanbanInstance && this.preference) {
            Runtime.loadExtension('ui.dialogs.checkbox-list').then(() => {
                kanbanInstance.fetchFields('view', kanbanInstance.getData().entityType, kanbanInstance.getData().customFields).then((data) => {
                    const newFields = [];
                    Object.entries(this.preference)?.forEach(([key, rule]) => {
                        if (Type.isArray(rule.conditions)) {
                            checkField(rule.conditions, newFields);
                        }
                    });

                    data.options.forEach(option => {
                        if (newFields.includes(option.id)) {
                            option.locked = true;
                            option.lockedBy = 'Palette';
                        }
                    });
                    kanbanInstance.fieldsSelectors.view = kanbanInstance.createFieldsSelector('view', data);
                });
            });
        }
    }

    modifyFieldsSelector(popup): void
    {
        const kanbanInstance = BX?.CRM?.Kanban?.Grid?.getInstance();
        if (kanbanInstance?.fieldsSelectors?.view?.isShown()) {
            const data = Kanban.fieldsSelectors.view.getOptions();

            if (!popup.lockedDescriptionAdded && data.find(option => option?.locked)) {
                Dom.append(
                    Tag.render`
                        <div class="ui-checkbox-list__container_desc-wrapper">
                            <div class="ui-checkbox-list__container_desc">
                                <div class="ui-checkbox-list__field-item_locked"></div>
                                <span>${Loc.getMessage('SIBIX_PALETTE_SETTINGS_FIELD_BLOCKED_FOR_MODIFICATION')}</span>
                            </div>
                        </div>
                    `,
                    popup.contentContainer.querySelector('div.ui-checkbox-list__wrapper div.ui-checkbox-list__container')
                );

                popup.lockedDescriptionAdded = true;
            }

            if (!popup.paletteDescriptionAdded && data.find(option => option?.locked && option?.lockedBy === 'Palette')) {
                data.forEach(option => {
                    if (option?.locked && option?.lockedBy === 'Palette') {
                        Dom.append(
                            Tag.render`<div class="ui-checkbox-list__field-item_locked-by-palette"></div>`,
                            popup.contentContainer.querySelector(`label.--disabled[title="${option.title}"]`)
                        );
                    }
                });

                Dom.append(
                    Tag.render`
                        <div class="ui-checkbox-list__container_desc">
                            <div class="ui-checkbox-list__field-item_locked-by-palette"></div>
                            <span>${Loc.getMessage('SIBIX_PALETTE_SETTINGS_FIELD_USED_BY_MODULE')}</span>
                        </div>
                    `,
                    popup.contentContainer.querySelector('div.ui-checkbox-list__wrapper div.ui-checkbox-list__container div.ui-checkbox-list__container_desc-wrapper')
                );

                popup.paletteDescriptionAdded = true;
            }
        }
    }

}